// frontend/modules/notification-config/components/NotificationTestModal.tsx
import React, { useState } from 'react';
import { 
  Modal, 
  Form,
  Input, 
  Button, 
  Space,
  Select,
  Tabs,
  Spin,
  Result,
  Typography,
  Divider,
  Alert,
  Card
} from 'antd';
import { CodeOutlined, SendOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useNotificationConfigs } from '../hooks/useNotificationConfigs';
import { NotificationTestResult, Languages, NotificationTestRequest } from '@/shared/api/model';

const { Option } = Select;
const { Text, Title } = Typography;
const { TextArea } = Input;

export const NotificationTestModal: React.FC = () => {
  const [form] = Form.useForm();
  const {
    selectedConfiguration,
    isTestModalVisible,
    setTestModalVisible,
    testConfig
  } = useNotificationConfigs();
  
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<NotificationTestResult | null>(null);
  const [activeTab, setActiveTab] = useState('1');
  
  // Sample context data for different platforms
  const sampleContexts = {
    EMAIL: `{
  "recipientName": "John Doe",
  "recipientEmail": "john.doe@example.com",
  "subject": "Test Email Notification",
  "institutionName": "ScaleFi SACCO",
  "portalLink": "https://portal.scalefi.com"
}`,
    SMS: `{
  "recipientName": "John Doe",
  "recipientPhone": "+255123456789",
  "institutionName": "ScaleFi SACCO"
}`,
    WHATS_APP: `{
  "recipientName": "John Doe",
  "recipientPhone": "+255123456789",
  "institutionName": "ScaleFi SACCO"
}`,
    IN_APP: `{
  "recipientName": "John Doe",
  "title": "Test In-App Notification",
  "message": "This is a test notification",
  "category": "SYSTEM",
  "priority": "INFORMATIONAL",
  "institutionName": "ScaleFi SACCO",
  "actionLink": "/dashboard"
}`
  };
  
  // Use sample context based on selected configuration's platform
  const getSampleContext = () => {
    if (!selectedConfiguration) return '';
    return sampleContexts[selectedConfiguration.platform] || '{}';
  };
  
  // Set initial form values
  const resetForm = () => {
    form.resetFields();
    setResult(null);
    setActiveTab('1');
    
    if (selectedConfiguration) {
      form.setFieldsValue({
        contextJson: getSampleContext(),
        language: selectedConfiguration.language
      });
    }
  };
  
  // Modal opening handler
  React.useEffect(() => {
    if (isTestModalVisible) {
      resetForm();
    }
  }, [isTestModalVisible, selectedConfiguration]);
  
  // Test execution handler
  const handleTest = async () => {
    if (!selectedConfiguration) return;
    
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      setResult(null);
      
      const testRequest: NotificationTestRequest = {
        configuration: selectedConfiguration,
        executionRequest: {
          contextJson: values.contextJson,
          language: values.language
        }
      };
      
      const response = await testConfig(testRequest);
      if (response) {
        setResult(response);
        setActiveTab('2'); // Switch to Results tab
      }
    } catch (error) {
      console.error('Error testing notification:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // JSON format validation
  const validateJSON = (_: any, value: string) => {
    if (!value) {
      return Promise.reject('Please enter context JSON');
    }
    
    try {
      JSON.parse(value);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject('Invalid JSON format');
    }
  };
  
  // Render recipient information
  const renderRecipientInfo = () => {
    if (!result || !result.resolvedRecipients) return null;
    
    const { resolvedRecipients } = result;
    
    return (
      <div className="mb-4">
        <Title level={5}>Recipient Information</Title>
        
        {resolvedRecipients.primaryRecipient && (
          <Alert
            message="Primary Recipient"
            description={
              <Space direction="vertical">
                <Text>
                  <strong>Platform:</strong> {resolvedRecipients.primaryRecipient.platform}
                </Text>
                <Text>
                  <strong>Contact:</strong> {resolvedRecipients.primaryRecipient.contactValue}
                </Text>
                {resolvedRecipients.primaryRecipient.userDisplayName && (
                  <Text>
                    <strong>Name:</strong> {resolvedRecipients.primaryRecipient.userDisplayName}
                  </Text>
                )}
              </Space>
            }
            type="info"
            showIcon
            className="mb-3"
          />
        )}
        
        <Space direction="vertical" className="w-full">
          {resolvedRecipients.allEmailAddresses && resolvedRecipients.allEmailAddresses.length > 0 && (
            <Card size="small" title={<><MailOutlined /> Email Recipients ({resolvedRecipients.allEmailAddresses.length})</>}>
              {resolvedRecipients.allEmailAddresses.map((email, index) => (
                <div key={`email-${index}`}>{email}</div>
              ))}
            </Card>
          )}
          
          {resolvedRecipients.allPhoneNumbers && resolvedRecipients.allPhoneNumbers.length > 0 && (
            <Card size="small" title={<><PhoneOutlined /> SMS Recipients ({resolvedRecipients.allPhoneNumbers.length})</>}>
              {resolvedRecipients.allPhoneNumbers.map((phone, index) => (
                <div key={`phone-${index}`}>{phone}</div>
              ))}
            </Card>
          )}
          
          {resolvedRecipients.allWhatsAppNumbers && resolvedRecipients.allWhatsAppNumbers.length > 0 && (
            <Card size="small" title={<><PhoneOutlined /> WhatsApp Recipients ({resolvedRecipients.allWhatsAppNumbers.length})</>}>
              {resolvedRecipients.allWhatsAppNumbers.map((whatsapp, index) => (
                <div key={`whatsapp-${index}`}>{whatsapp}</div>
              ))}
            </Card>
          )}
        </Space>
        
        {resolvedRecipients.recipientCountByPlatform && (
          <div className="mt-3">
            <Text type="secondary">
              Recipient Count by Platform: {' '}
              {Object.entries(resolvedRecipients.recipientCountByPlatform)
                .map(([platform, count]) => `${platform}: ${count}`)
                .join(', ')}
            </Text>
          </div>
        )}
        
        {resolvedRecipients.errorMessage && (
          <Alert 
            message="Recipient Resolution Error" 
            description={resolvedRecipients.errorMessage}
            type="error" 
            showIcon 
            className="mt-3" 
          />
        )}
      </div>
    );
  };
  
  // Render transformed context
  const renderTransformedContext = () => {
    if (!result?.transformation?.transformedContext) return null;
    
    return (
      <div className="mb-4">
        <Title level={5}>Transformed Context</Title>
        <Card size="small">
          <pre className="whitespace-pre-wrap">
            {JSON.stringify(result.transformation.transformedContext, null, 2)}
          </pre>
        </Card>
      </div>
    );
  };
  
  // Render processed content
  const renderProcessedContent = () => {
    if (!result) return null;
    
    return (
      <div className="mb-4">
        <Title level={5}>Processed Content</Title>
        {result.processedSubject && (
          <div className="mb-2">
            <Text strong>Subject:</Text>
            <div className="p-2 border rounded bg-gray-50 dark:bg-gray-800">
              {result.processedSubject}
            </div>
          </div>
        )}
        <div>
          <Text strong>Content:</Text>
          <div className="p-2 border rounded bg-gray-50 dark:bg-gray-800">
            {result.processedContent ? (
              <div dangerouslySetInnerHTML={{ __html: result.processedContent.replace(/\n/g, '<br/>') }} />
            ) : (
              <Text type="secondary">No content processed</Text>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Render error message
  const renderError = () => {
    if (!result || result.overallSuccess) return null;
    
    return (
      <Alert
        type="error"
        message="Error during notification processing"
        description={result.message}
        showIcon
      />
    );
  };

  // Define tab items using the new Ant Design approach
  const tabItems = [
    {
      key: '1',
      label: 'Configure Test',
      children: (
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="contextJson"
            label="Sample Context JSON"
            rules={[
              { required: true, message: 'Please enter context JSON' },
              { validator: validateJSON }
            ]}
            tooltip="JSON data that will be passed to the context script"
          >
            <TextArea 
              rows={12} 
              placeholder="Enter JSON context data"
              className="font-mono"
            />
          </Form.Item>
          
          <Form.Item
            name="language"
            label="Language"
            rules={[{ required: true }]}
            tooltip="Override the default language if needed"
          >
            <Select placeholder="Select language">
              <Option value={Languages.EN}>English</Option>
              <Option value={Languages.SW}>Swahili</Option>
            </Select>
          </Form.Item>
          
          <Alert
            type="info"
            message="This will process the notification with the sample context data but won't send any actual messages."
            showIcon
          />
        </Form>
      ),
    },
    {
      key: '2',
      label: 'Results',
      disabled: !result,
      children: (
        <Spin spinning={isLoading}>
          {result ? (
            <div>
              {result.overallSuccess ? (
                <>
                  {renderRecipientInfo()}
                  <Divider />
                  {renderProcessedContent()}
                  <Divider />
                  {renderTransformedContext()}
                  
                  <div className="text-right text-gray-500 mt-4">
                    <Text type="secondary">
                      Execution time: {result.transformation?.executionTimeMs || 0}ms
                    </Text>
                  </div>
                </>
              ) : (
                <Result
                  status="error"
                  title="Processing Failed"
                  subTitle={result.message || 'An unknown error occurred.'}
                  extra={[
                    <Button 
                      key="back" 
                      onClick={() => setActiveTab('1')}
                    >
                      Edit Context
                    </Button>
                  ]}
                >
                  {renderError()}
                </Result>
              )}
            </div>
          ) : (
            <div className="text-center p-10">
              <Text type="secondary">Run a test to see results</Text>
            </div>
          )}
        </Spin>
      ),
    },
  ];
  
  return (
    <Modal
      title={`Test Notification: ${selectedConfiguration?.name || ''}`}
      open={isTestModalVisible}
      onCancel={() => setTestModalVisible(false)}
      width={800}
      footer={[
        <Button key="close" onClick={() => setTestModalVisible(false)}>
          Close
        </Button>,
        <Button 
          key="test" 
          type="primary"
          icon={<SendOutlined />}
          onClick={handleTest}
          disabled={!selectedConfiguration}
          loading={isLoading}
        >
          Test Notification
        </Button>
      ]}
    >
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={tabItems}
      />
    </Modal>
  );
};
